/**
 * Feature Flags Configuration
 *
 * Use these flags to enable/disable features across the application.
 * Set to `true` to enable a feature, `false` to disable it.
 */
const ENABLED_FLAG_VALUES = new Set(["1", "true", "yes", "on"]);

function isEnvFlagEnabled(value: string | undefined): boolean {
  if (!value) return false;
  return ENABLED_FLAG_VALUES.has(value.trim().toLowerCase());
}

export const FEATURES = {
  /**
   * SOP Library feature - includes:
   * - SOP Library page and navigation
   * - SOP-related dashboard widgets
   * - SOP data fetching and realtime subscriptions
   *
   * Set to `true` when ready to launch SOP functionality.
   */
  SOP_LIBRARY: false,

  /**
   * Zapier Partner API Integration - enables OAuth-based Zap creation
   * directly from automation guides via Zapier's Partner API.
   *
   * Requires:
   * - Zapier Partner/Embed approval
   * - ZAPIER_CLIENT_ID and ZAPIER_CLIENT_SECRET secrets configured
   *
   * When disabled: "Create Zap" button uses simple pre-filled URL approach
   * When enabled: Full OAuth flow with direct Zap creation via API
   *
   * STATUS: Enabled (approved January 2026)
   */
  ZAPIER_PARTNER_API: true,

  /**
   * Perplexity Feasibility Check - uses Perplexity-powered feasibility check
   * that verifies apps against actual Zapier documentation.
   *
   * Features:
   * - Uses Perplexity sonar-reasoning-pro with web search
   * - Returns PASS/FAIL/NEEDS_INFO status
   * - Verifies apps have actual Zapier integrations
   * - Single API call for feasibility assessment
   *
   * This is the production automation assessment flow.
   */
  PERPLEXITY_FEASIBILITY_CHECK: true,

  /**
   * DeepL Translation - enables AI-powered translation of automation guides
   * from English to Spanish (and vice versa) using DeepL API.
   *
   * Features:
   * - Translates automation guide content via DeepL API
   * - Preserves markdown formatting during translation (using ignore_tags=x)
   * - Caches translations in content_translations table
   * - Toggle to view original/translated content
   * - Works in both full markdown view and step-by-step view
   *
   * Requires:
   * - DEEPL_API_KEY secret configured in Supabase
   *
   * When disabled: Users see original content only, no translation UI shown
   * When enabled: Translation badge and toggle appear for non-source languages
   *
   * STATUS: Billing paused, default OFF until explicitly re-enabled
   * via VITE_DEEPL_TRANSLATION_ENABLED=true.
   */
  DEEPL_TRANSLATION: isEnvFlagEnabled(
    import.meta.env.VITE_DEEPL_TRANSLATION_ENABLED,
  ),

  /**
   * Allow Signups - controls whether new user registration is enabled.
   *
   * When disabled:
   * - Auth page shows "signups paused" message instead of signup form
   * - Landing page "Get Started" button shows "Coming Soon" toast
   * - Existing users can still sign in normally
   *
   * When enabled: Normal signup flow is active
   *
   * STATUS: Disabled (signup flow paused)
   */
  ALLOW_SIGNUPS: false,

  /**
   * DFY Automation Purchase - enables "Get This Built For You" button
   * on automation guides with Stripe Checkout integration.
   *
   * Features:
   * - Price quote based on automation complexity
   * - Stripe Checkout session for payment
   * - Order tracking in dfy_orders table
   * - Transparent pricing breakdown
   *
   * Requires:
   * - STRIPE_SECRET_KEY secret in Supabase
   * - SUPABASE_SERVICE_ROLE_KEY in Vercel env (for webhook)
   * - dfy_orders table migration applied
   *
   * When disabled: No DFY button shown, no pricing calculations
   * When enabled: DFY button appears in guide detail header
   *
   * STATUS: Enabled (February 2026)
   */
  DFY_AUTOMATION: true,

  /**
   * PDF Export - enables exporting automation guides as PDF documents
   * using the `export-pdf` edge function.
   *
   * Requires:
   * - APITEMPLATE_API_KEY secret in Supabase
   * - PDF_EXPORT_ENABLED=true secret in Supabase for backend enablement
   *
   * When disabled: PDF button is hidden and admin monthly PDF limit card is hidden
   * When enabled: PDF export UI and admin monthly PDF limit tracking card are shown
   *
   * STATUS: Disabled (cost-control pause for APITemplate)
   */
  PDF_EXPORT: false,
} as const;

export type FeatureFlags = typeof FEATURES;
